import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { images } from './core/_models'
import { PageLink, PageTitle } from '../../../../_dashboard/layout/core'
import { ID, isNotEmpty, KTCard, KTCardBody, QUERIES } from '../../../../_dashboard/helpers'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TagsComponent from '../../../../_dashboard/partials/widgets/tags/TagsComponent'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import _ from 'lodash'
import ImageUploader from '../../../../_dashboard/partials/widgets/upload/ImageUploader'
import { useQuery } from 'react-query'
import { deleteOne, getOneById, getOneByIdbylang, patchOne } from '../../../services/common_requests'
import { useListView } from '../hotel-list/core/ListViewProvider'
import ImageEditModal from './image-edit-modal/ImageEditModal'
import { Facility } from '../hotel-list/core/_models'
import { useQueryResponse } from '../hotel-list/core/QueryResponseProvider'
import { useIntl } from 'react-intl'
import { toast, ToastContainer } from 'react-toastify'
import { LanguageSelector } from '../../pages-managment/pages-details/commun-component/LanguageSelector'
import { useAuth } from '../../../modules/auth'
import { DetailPageBack } from '../../../../_dashboard/partials/components/DetailPageBack'
import { ImageDimensionHint } from '../../../../_dashboard/partials/components/ImageDimensionHint'

const SHORT_DESCRIPTION_MAX = 300

const stripHtml = (html: string) => (html || '').replace(/<[^>]*>/g, '').trim()

export interface DeleteResponse {
  payload?: {
    code?: number
  }
}
const HotelDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: any }>()
  const [selectedImage, setSelectedImage] = useState<images | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('fr')

  const handleImageClick = (img: images) => {
    setSelectedImage(img)
    setIsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }
  const { refetchData } = useQueryResponse()
  const {auth} = useAuth()

  const handleUpdateImage = async (updatedImage: { url: string; seoUrl?: string }) => {
    // Update the album in formik state
    const updatedAlbum = formik.values.Album.map((img: images) =>
      img.url === selectedImage?.url
        ? { ...img, url: updatedImage.url, seoUrl: updatedImage.seoUrl || '' }
        : img
    )
    formik.setFieldValue('Album', updatedAlbum)

    // Create form data with the updated image only
    const formData = new FormData()
    if (updatedImage.url !== selectedImage?.url) {
      const response = await fetch(updatedImage.url)
      const blob = await response.blob()
      const file = new File([blob], 'updated-image.jpg', { type: blob.type })
      formData.append('image', file)
    }
    if (updatedImage.seoUrl) {
      formData.append('seoUrl', updatedImage.seoUrl)
    }
    setImages(updatedAlbum)
    try {
      // Call patchOne with only the updated image
      await patchOne(formData, `/images/${selectedImage?._id}`).then((response) => {
        if (response?.payload?.code === 2021) {
          //refetchData()
          handleCloseModal()
          toast.success(
            intl.formatMessage({
              id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        } else {
          toast.error(
            intl.formatMessage({
              id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        }
      })
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }
  const hotelsBreadcrumbs: Array<PageLink> = [
    {
      title: 'Hotel Management',
      path: '/apps/hotel-management/hotels',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ]

  const [uploadedImage, setUploadedImage] = useState<File | null>(null)
  const [images, setImages] = useState<images[]>([])

  const handleImageAdded = (newImage: { url: string; _id: string }) => {
    setImages((prevImages) => [
      ...prevImages,
      {
        ...newImage,
        seoUrl: '',
        _id: newImage._id as unknown as ID,
      },
    ])
  }

  const intl = useIntl()

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(intl.formatMessage({ id: 'VALIDATION.EMAIL' })),
    address: Yup.string(),
    seoSlug: Yup.string(),
    translations: Yup.array().of(
      Yup.object().shape({
        name: Yup.string(),
        shortDescription: Yup.string().test(
          'max-plain-length',
          intl.formatMessage({ id: 'VALIDATION.SHORT_DESCRIPTION_MAX', defaultMessage: `Maximum ${SHORT_DESCRIPTION_MAX} caractères` }),
          (value) => stripHtml(value || '').length <= SHORT_DESCRIPTION_MAX
        ),
        longDescription: Yup.string(),
        note: Yup.string(),
        metaDescription: Yup.string(),
        metaTitle: Yup.string(),
        metaKeywords: Yup.array().of(Yup.string()),
      })
    ),
    seoKeywords: Yup.array().of(Yup.string()),
    phone: Yup.string().matches(
      /^\+216\s\d{2}\s\d{3}\s\d{3}$/,
      intl.formatMessage({ id: 'VALIDATION.PHONE' })
    ),
  })
  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const enabledQuery: boolean = isNotEmpty(id)
  const { data: hotels, refetch } = useQuery(
    `${QUERIES.HOTELS_LIST}-hotel-${id}-${selectedLanguage}`,
    () => {
      if (!id) {
        throw new Error('ID is undefined')
      }
      return getOneByIdbylang(`/hotels/${id}?language=${selectedLanguage}`)
    },
    {
      cacheTime: 0,
      enabled: enabledQuery,
      onError: (err) => {
        setItemIdForUpdate(undefined)
        console.error(err)
      },
    }
  )

  useEffect(() => {
    if (id) {
      refetch()
    }
  }, [selectedLanguage])

  const initialValues = hotels
    ? {
      id: hotels._id,
      phone: hotels.phone,
      email: hotels.email,
      ratings: hotels.ratings.title || { title: '', star: 0 },
      address: hotels.address,
      Facilities: hotels.facilities,
      facilitySettings: hotels.facilitySettings?.length
        ? hotels.facilitySettings
        : (hotels.facilities || []).map((f: Facility, i: number) => ({
            facilityId: f._id,
            isVisible: true,
            order: i,
          })),
      options: hotels.options,
      themes: hotels.themes,
      Album: hotels.images,
      seoSlug: hotels?.seoSlug,
      translations: hotels?.translations || [
        {
          shortDescription: '',
          note: '',
          name: '',
          longDescription: '',
          metaDescription: hotels.metaDescription || '',
          metaTitle: hotels.metaTitle || '',
          metaKeywords: hotels.metaKeywords || [],
        }
      ],
    }
    : {
      id: '',
      Name: '',
      phone: '',
      email: '',
      ratings: { Ttitle: '', star: 0 },
      address: '',
      shortDescription: '',
      Facilities: [],
      facilitySettings: [],
      options: [],
      themes: [],
      Note: '',
      Album: [],
      translations: [
        {
          shortDescription: '',
          note: '',
          name: '',
          longDescription: '',
          metaDescription: '',
          metaTitle: '',
          metaKeywords: [],
        }
      ],
    }

  // Initialisation de Formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const modifiedFields = Object.keys(values).reduce((acc, key) => {
        const typedKey = key as keyof typeof values
        if (!_.isEqual(values[typedKey], initialValues[typedKey])) {
          if (key === 'options' || (key === 'themes' && Array.isArray(values[typedKey]))) {
            acc[typedKey] = values[typedKey].map((option: { _id: string }) => option._id)
          } else if (key === 'facilitySettings') {
            acc[typedKey] = values[typedKey]
          } else if (key === 'Facilities') {
            // read-only display field, not sent to API
          } else {
            acc[typedKey] = values[typedKey]
          }
        }
        return acc
      }, {} as Record<string, any>)
      const formData = new FormData()
      if (uploadedImage) {
        formData.append('image', uploadedImage)
      }
      Object.keys(modifiedFields).forEach((key) => {
        formData.append(key, modifiedFields[key])
      })

      try {
        await patchOne(modifiedFields, `/hotels/${values.id}`).then((response) => {
          if (response?.payload?.code === 2021) {
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
            refetchData()
          } else {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
          }
        })
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    },
  })
  useEffect(() => {
    if (hotels) {
      formik.setValues({
        id: hotels?._id,
        phone: hotels.phone || '',
        email: hotels.email || '',
        ratings: hotels.ratings || { title: '', star: 0 },
        address: hotels.address || '',
        Facilities: hotels.facilities || [],
        facilitySettings: hotels.facilitySettings?.length
          ? hotels.facilitySettings
          : (hotels.facilities || []).map((f: Facility, i: number) => ({
              facilityId: f._id,
              isVisible: true,
              order: i,
            })),
        options: hotels.options || [],
        themes: hotels.themes || [],
        Album: hotels.images || [],
        seoSlug: hotels?.seoSlug || '',
        translations: hotels?.translations || [
          {
            shortDescription: '',
            note: '',
            name: '',
            longDescription: '',
            metaDescription: hotels.metaDescription || '',
            metaTitle: hotels.metaTitle || '',
            metaKeywords: hotels.metaKeywords || [],
          }
        ],
      })
    }
  }, [hotels])

  useEffect(() => {
    if (hotels?.images) {
      setImages(hotels.images)
    }
  }, [hotels])
  const handleDeleteImage = async (img: images) => {
    try {
      await deleteOne(img._id, '/images').then((response: DeleteResponse) => {
        if (response?.payload?.code === 2020) {
          setImages((prevImages) => prevImages.filter((image) => image._id !== img._id))
          toast.success(
            intl.formatMessage({
              id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        } else {
          toast.error(
            intl.formatMessage({
              id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        }
      })
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }

  const handleSetMainImage = async (imageId: string) => {
    if (!id || (auth?.role !== 'ROLE_SUPER_ADMIN' && auth?.role !== 'ROLE_ADMIN')) {
      return
    }
    try {
      const response = await patchOne({image: imageId}, `/hotels/${id}`)
      if (response?.payload?.code === 2021) {
        toast.success(
          intl.formatMessage({
            id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
        refetch()
      } else {
        toast.error(
          intl.formatMessage({
            id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }
  return (
    <>
      <ToastContainer />

      <PageTitle breadcrumbs={hotelsBreadcrumbs}>
        {intl.formatMessage({
          id: 'ADMIN.COMMON.HOTEL_DETAILS',
        })}
      </PageTitle>
      <DetailPageBack to='/apps/hotel-management/hotels' />
      <KTCard>
        <KTCardBody className='py-4'>
          <div className='py-4 app-content flex-column-fluid'>
            <h1 className='my-2'>{hotels?.translations?.[0]?.name}</h1>
            <span className='text-muted right-spacing '>
              {intl.formatMessage({
                id: 'ADMIN.COMMON.HOTEL_ID',
              })}{' '}
              {hotels?.hotel_id}
            </span>
            <section className='gallery-section py-4'>
              <ImageDimensionHint entity='hotelGalleryLarge' />
              <ImageDimensionHint entity='hotelGalleryThumb' />
              <div className='gallery gallery-style'>
                {images.map((img: images, index: number) => (
                  <div key={index} className='d-flex flex-row justify-content-space-between'>
                    <img
                      src={
                        img.url.includes('/images/')
                          ? process.env.REACT_APP_API_URL_IMAGE + img.url
                          : img.url
                      }
                      alt={`Gallery ${index}`}
                      className='border-image h-175px cursor-pointer'
                      onClick={() => handleImageClick(img)}
                    />
                    <div className='d-flex flex-column justify-content-space-between'>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-warning w-25px h-25px bg-body shadow'
                        data-bs-toggle='tooltip'
                        title='Set as main image'
                      >
                        <i
                          className={`cursor-pointer bi ${
                            hotels?.image?._id?.toString() === img._id?.toString()
                              ? 'bi-star-fill'
                              : 'bi-star'
                          } fs-7 btn btn-icon btn-circle btn-active-color-warning`}
                          style={{opacity: (auth?.role === 'ROLE_SUPER_ADMIN' || auth?.role === 'ROLE_ADMIN') ? 1 : 0.5}}
                          onClick={() => (auth?.role === 'ROLE_SUPER_ADMIN' || auth?.role === 'ROLE_ADMIN') && handleSetMainImage(String(img._id))}
                        ></i>
                      </span>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-kt-image-input-action='remove'
                        data-bs-toggle='tooltip'
                        title='Remove icon'
                      >
                        <i
                          className='cursor-pointer bi bi-pencil-fill fs-7 btn btn-icon btn-circle btn-active-color-primary'
                          onClick={() => handleImageClick(img)}
                        ></i>
                      </span>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-kt-image-input-action='remove'
                        data-bs-toggle='tooltip'
                        title='Remove icon'
                      >
                        <i
                          className='cursor-pointer bi bi-x fs-2 btn btn-icon btn-circle btn-active-color-primary'
                          onClick={() => handleDeleteImage(img)}
                        ></i>
                      </span>
                    </div>
                  </div>
                ))}
                <ImageUploader
                  onImageUpload={() => {}}
                  onImageAdded={handleImageAdded}
                  uploadEndpoint='/images'
                  hotelId={hotels?._id}
                />
              </div>
            </section>

            <ImageEditModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              selectedImage={selectedImage || { url: '', seoUrl: '' }}
              onSave={handleUpdateImage}
            />

            <div className='separator mb-4'></div>
            <div className='row'>
              <div className='col-md-12'>
                <h3>
                  {' '}
                  {intl.formatMessage({
                    id: 'ADMIN.COMMON.GENERAL_INFORMATION',
                  })}
                </h3>
                <LanguageSelector
                  selected={selectedLanguage}
                  onChange={(lang) => {
                    setSelectedLanguage(lang)
                  }}
                />
                <form onSubmit={formik.handleSubmit}>
                  <div className='form-group mb-4'>
                    <div className='d-flex flex-wrap align-items-center'>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.CATEGORY',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='ratings.title'
                          value={formik.values.ratings.title}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled
                        />
                        {formik.touched.ratings &&
                          typeof formik.touched.ratings === 'object' &&
                          'title' in formik.touched.ratings &&
                          formik.errors.ratings &&
                          typeof formik.errors.ratings === 'object' &&
                          'title' in formik.errors.ratings && (
                            <div className='text-danger'>
                              {formik.errors.ratings.title as string}
                            </div>
                          )}
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.ADDRESS',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='address'
                          value={formik.values.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.address && formik.errors.address && (
                          <div className='text-danger'>{formik.errors.address as string}</div>
                        )}
                      </div>

                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.NAME',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][name]'
                          value={formik.values.translations?.[0]?.name || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.Name && formik.errors.Name && (
                          <div className='text-danger'>{formik.errors.Name as string}</div>
                        )}
                      </div>


                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.PHONE',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='phone'
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.phone && formik.errors.phone && (
                          <div className='text-danger'>{formik.errors.phone as string}</div>
                        )}
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.EMAIL',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='email'
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <div className='text-danger'>{formik.errors.email as string}</div>
                        )}
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {intl.formatMessage({
                              id: 'COLUMNS.METE_TITLE',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][metaTitle]'
                          value={formik.values.translations?.[0]?.metaTitle || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {Array.isArray(formik.errors.translations) &&
                          formik.errors.translations[0] &&
                          typeof formik.errors.translations[0] === 'object' &&
                          'metaTitle' in formik.errors.translations[0] && (
                            <div className='text-danger'>
                              {formik.errors.translations[0].metaTitle as string}
                            </div>
                          )}
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.OPEN_GRAPHE_TITLE',
                          })}</strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][openGraphTitle]'
                          value={formik.values.translations?.[0]?.openGraphTitle || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>

                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.OPEN_GRAPHE_IMAGE',
                          })}</strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][openGraphImage]'
                          value={formik.values.translations?.[0]?.openGraphImage || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.OPEN_GRAPHE_TYPE',
                          })}</strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][openGraphType]'
                          value={formik.values.translations?.[0]?.openGraphType || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.TWITTER_TITLE',
                          })}</strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][twitterTitle]'
                          value={formik.values.translations?.[0]?.twitterTitle || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>

                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.TWITTER_IMAGE',
                          })}</strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='translations[0][twitterImage]'
                          value={formik.values.translations?.[0]?.twitterImage || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='w-100 mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.TWITTER_DESCRIPTION',
                          })}</strong>
                        </label>
                        <textarea
                          className='form-control w-100 border-sm'
                          name='translations[0][twitterDescription]'
                          value={formik.values.translations?.[0]?.twitterDescription || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          rows={3}
                        />
                      </div>
                      <div className='w-100 mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>{intl.formatMessage({
                            id: 'COLUMNS.OPEN_GRAPHE_DESCRIPTION',
                          })}</strong>
                        </label>
                        <textarea
                          className='form-control w-100 border-sm'
                          name='translations[0][openGraphDescription]'
                          value={formik.values.translations?.[0]?.openGraphDescription || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          rows={3}
                        />
                      </div>
                      <div className='w-100 mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {intl.formatMessage({
                              id: 'COLUMNS.METE_DESCRIPTION',
                            })}
                          </strong>
                        </label>
                        <textarea
                          className='form-control w-100 border-sm'
                          name='translations[0][metaDescription]'
                          value={formik.values.translations?.[0]?.metaDescription || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {Array.isArray(formik.errors.translations) &&
                          formik.errors.translations[0] &&
                          typeof formik.errors.translations[0] === 'object' &&
                          'metaDescription' in formik.errors.translations[0] && (
                            <div className='text-danger'>
                              {formik.errors.translations[0].metaDescription as string}
                            </div>
                          )}
                      </div>

                      <div className='form-group mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.SHORT_DESCRIPTION',
                            })}
                          </strong>
                        </label>
                        <ReactQuill
                          value={formik.values.translations?.[0]?.shortDescription || ''}
                          onChange={(value) =>
                            formik.setFieldValue('translations[0][shortDescription]', value)
                          }
                          onBlur={() =>
                            formik.setFieldTouched('translations[0][shortDescription]', true)
                          }
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              ['blockquote'],
                              [{ header: 1 }, { header: 2 }],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ indent: '-1' }, { indent: '+1' }],
                              [{ direction: 'rtl' }],
                              [{ size: ['small', false, 'large', 'huge'] }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              [{ font: [] }],
                              ['clean'],
                            ],
                          }}
                          formats={[
                            'header',
                            'font',
                            'size',
                            'bold',
                            'italic',
                            'underline',
                            'strike',
                            'blockquote',
                            'list',
                            'bullet',
                            'indent',
                            'link',
                            'image',
                            'video',
                          ]}
                        />
                        <div className={`form-text ${stripHtml(formik.values.translations?.[0]?.shortDescription || '').length > SHORT_DESCRIPTION_MAX ? 'text-danger' : 'text-muted'}`}>
                          {stripHtml(formik.values.translations?.[0]?.shortDescription || '').length} / {SHORT_DESCRIPTION_MAX} caractères (texte visible côté front)
                        </div>
                      </div>
                      <div className='form-group mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.LONG_DESCRIPTION',
                            })}
                          </strong>
                        </label>
                        <ReactQuill
                          value={formik.values.translations?.[0]?.longDescription || ''}
                          onChange={(value) =>
                            formik.setFieldValue('translations[0][longDescription]', value)
                          }
                          onBlur={() =>
                            formik.setFieldTouched('translations[0][longDescription]', true)
                          }
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              ['blockquote'],
                              [{ header: 1 }, { header: 2 }],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ indent: '-1' }, { indent: '+1' }],
                              [{ direction: 'rtl' }],
                              [{ size: ['small', false, 'large', 'huge'] }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              [{ font: [] }],
                              ['clean'],
                            ],
                          }}
                          formats={[
                            'header',
                            'font',
                            'size',
                            'bold',
                            'italic',
                            'underline',
                            'strike',
                            'blockquote',
                            'list',
                            'bullet',
                            'indent',
                            'link',
                            'image',
                            'video',
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.FACILITIES',
                    })}
                  </h3>
                  <div className='mb-3'>
                    {formik.values.Facilities.map((facility: Facility, index: number) => {
                      const setting = formik.values.facilitySettings?.find(
                        (s: { facilityId: string }) => String(s.facilityId) === String(facility._id)
                      )
                      const isVisible = setting ? setting.isVisible !== false : true
                      return (
                        <div key={facility._id || index} className='d-flex align-items-center justify-content-between mb-2 p-2 border rounded'>
                          <span>{facility?.translations?.[0]?.title || ''}</span>
                          <div className='form-check form-switch'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={isVisible}
                              onChange={(e) => {
                                const updated = [...(formik.values.facilitySettings || [])]
                                const idx = updated.findIndex((s) => String(s.facilityId) === String(facility._id))
                                if (idx >= 0) {
                                  updated[idx] = { ...updated[idx], isVisible: e.target.checked }
                                } else {
                                  updated.push({ facilityId: facility._id, isVisible: e.target.checked, order: index })
                                }
                                formik.setFieldValue('facilitySettings', updated)
                              }}
                            />
                            <label className='form-check-label ms-2'>
                              {isVisible ? 'Visible' : 'Masqué'}
                            </label>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.META_KEYWORDS',
                    })}
                  </h3>
                  <div className=' mb-4'>
                    <TagsComponent
                      initialValues={formik.values.translations?.[0]?.metaKeywords || []}
                      onValuesChange={(updatedMetaKeywords) => {
                        formik.setFieldValue('translations[0][metaKeywords]', updatedMetaKeywords)
                      }}
                      type='metaKeywords'
                      selectedLanguage={selectedLanguage}

                    />
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.THEMES',
                    })}
                  </h3>
                  <div className=' mb-4'>
                    <TagsComponent

                      initialValues={formik.values.themes?.map(
                        (theme: { _id: string; translations?: { name: string }[] }) =>
                          theme._id
                      ) || []}
                      onValuesChange={(updatedthemes) => {
                        const newThemes = updatedthemes.map(opt => {
                          const existing = formik.values.themes.find((o: { _id: string; translations?: { name: string }[] }) => o._id === opt);
                          const fromAvailable = { _id: opt, translations: [{ name: opt }] };
                          return existing || fromAvailable;
                        });
                        formik.setFieldValue('themes', newThemes);
                      }}
                      type='themes'
                      selectedLanguage={selectedLanguage}
                    />
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.OPTIONS',
                    })}
                  </h3>
                  <div className=' mb-4'>
                    <TagsComponent
                      initialValues={formik.values.options?.map(
                        (option: { _id: string; translations?: { title: string }[] }) =>
                          option._id
                      ) || []}
                      onValuesChange={(updatedOptions) => {
                        const newOptions = updatedOptions.map(opt => {
                          const existing = formik.values.options.find((o: { _id: string; translations?: { title: string }[] }) => o._id === opt);
                          return existing || { _id: opt, translations: [{ title: opt }] };
                        });
                        formik.setFieldValue('options', newOptions);
                      }}
                      selectedLanguage={selectedLanguage}
                      type='options'
                    />
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.NOTE',
                    })}
                  </h3>
                  <div className='form-group mb-3'>
                    <ReactQuill
                      value={formik.values.translations?.[0]?.note || ''}
                      onChange={(value) => formik.setFieldValue('translations[0][note]', value)}
                      onBlur={() => formik.setFieldTouched('translations[0][note]', true)}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          ['blockquote'],
                          [{ header: 1 }, { header: 2 }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          [{ indent: '-1' }, { indent: '+1' }],
                          [{ direction: 'rtl' }],
                          [{ size: ['small', false, 'large', 'huge'] }],
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          [{ font: [] }],
                          ['clean'],
                        ],
                      }}
                      formats={[
                        'header',
                        'font',
                        'size',
                        'bold',
                        'italic',
                        'underline',
                        'strike',
                        'blockquote',
                        'list',
                        'indent',
                        'link',
                        'image',
                        'video',
                      ]}
                    />
                    <div className='form-text text-muted'>
                      {intl.formatMessage({
                        id: 'COLUMNS.NOTE_HELP',
                        defaultMessage: 'Cette note est réservée à l\'équipe interne et n\'apparaît pas sur le site public.',
                      })}
                    </div>
                    {formik.touched.Note && formik.errors.Note && (
                      <div className='text-danger'>{formik.errors.Note as string}</div>
                    )}
                  </div>
                  <div className='separator mb-4'></div>
                  <div className='modal-footer'>
                    <button type='submit' className='btn btn-primary me-2'>
                      {intl.formatMessage({
                        id: 'HEADER.SAVE',
                      })}
                    </button>
                    <button type='button' className='btn btn-secondary '>
                      {intl.formatMessage({
                        id: 'HEADER.RESET',
                      })}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export { HotelDetailsPage }
